export interface GridClasses {
  /**
   * Styles applied to the root element.
   */
  root: string
  /**
   * Styles applied to the root element if density is "standard" (default).
   */
  'root--densityStandard': string
  /**
   * Styles applied to the root element if density is "comfortable".
   */
  'root--densityComfortable': string
  /**
   * Styles applied to the root element if density is "compact".
   */
  'root--densityCompact': string
  /**
   * Styles applied to the root element if `autoHeight={true}`.
   */
  autoHeight: string
  /**
   * Styles applied to the main container element.
   */
  main: string
  /**
   * Styles applied to the column headers.
   */
  columnHeaders: string
  /**
   * Styles applied to the column headers's inner element.
   */
  columnHeadersInner: string
  /**
   * Styles applied to the column headers's inner element if there is a horizontal scrollbar.
   */
  'columnHeadersInner--scrollable': string
  /**
   * Styles applied to the pinned column headers.
   */
  pinnedColumnHeaders: string
  /**
   * Styles applied to the left pinned column headers.
   */
  'pinnedColumnHeaders--left': string
  /**
   * Styles applied to the right pinned column headers.
   */
  'pinnedColumnHeaders--right': string
  columnHeaderRow: string

  /**
   * Styles applied to the column header element.
   */
  columnHeader: string
  /**
   * Styles applied to the column header if `headerAlign="center"`.
   */
  'columnHeader--alignCenter': string
  /**
   * Styles applied to the column header if `headerAlign="left"`.
   */
  'columnHeader--alignLeft': string
  /**
   * Styles applied to the column header if `headerAlign="right"`.
   */
  'columnHeader--alignRight': string
  /**
   * Styles applied to the column header if the column is sortable.
   */
  'columnHeader--sortable': string
  /**
   * Styles applied to the column header if the column is sorted.
   */
  'columnHeader--sorted': string
  /**
   * Styles applied to the column header if it is being dragged.
   */
  'columnHeader--moving': string
  /**
   * Styles applied to the floating column header element when it is dragged.
   */
  'columnHeader--dragging': string
  /**
   * Styles applied to the column header's draggable container element.
   */
  columnHeaderDraggableContainer: string
  /**
   * Styles applied to the column header's title container element.
   */
  columnHeaderTitleContainer: string
  /**
   * Styles applied to the column header's title excepted buttons.
   */
  columnHeaderTitleContainerContent: string
  /**
   * Styles applied to the column header's title element;
   */
  columnHeaderTitle: string
  /**
   * Styles applied to the column header's button container element.
   */
  columnHeaderButtonContainer: string
  /**
   * Styles applied to the column header separator element.
   */
  columnSeparator: string
  /**
   * Styles applied to the column header separator if the column is resizable.
   */
  'columnSeparator--resizable': string
  /**
   * Styles applied to the column header separator if the column is being resized.
   */
  'columnSeparator--resizing': string
  /**
   * Styles applied to the column header separator if the side is "left".
   */
  'columnSeparator--sideLeft': string
  /**
   * Styles applied to the column header separator if the side is "right".
   */
  'columnSeparator--sideRight': string
  /**
   * Styles applied to the virtualization container.
   */
  virtualScroller: string
  /**
   * Styles applied to the virtualization content.
   */
  virtualScrollerContent: string
  /**
   * Styles applied to the virtualization render zone.
   */
  virtualScrollerRenderZone: string
  /**
   * Styles applied to the pinned columns.
   */
  pinnedColumns: string
  /**
   * Styles applied to the left pinned columns.
   */
  'pinnedColumns--left': string
  /**
   * Styles applied to the right pinned columns.
   */
  'pinnedColumns--right': string

  /**
   * Styles applied to the column footer Row element.
   */
  columnFooterRow: string
  /**
   * Styles applied to the Skeleton Row element.
   */
  skeletonRow: string
  /**
   * Styles applied to the row element.
   */
  row: string
  /**
   * Styles applied to the row element if the row is editable.
   */
  'row--editable': string
  /**
   * Styles applied to the row element if the row is in edit mode.
   */
  'row--editing': string
  /**
   * Styles applied to the row if its selected.
   */
  'row--selected': string
  /**
   * Styles applied to the row if its hovered (for column-pinning use cases).
   */
  'row--hovered': string
  /**
   * Styles applied to the row if it has dynamic row height.
   */
  'row--dynamicHeight': string
  /**
   * Styles applied to the cell element.
   */
  cell: string
  /**
   * Styles applied to the cell element if `align="center"`.
   */
  'cell--textCenter': string
  /**
   * Styles applied to the cell element if `align="left"`.
   */
  'cell--textLeft': string
  /**
   * Styles applied to the cell element if `align="right"`.
   */
  'cell--textRight': string
  /**
   * Styles applied to the cell element if the cell is editable.
   */
  'cell--editable': string
  /**
   * Styles applied to the cell element if the cell is in edit mode.
   */
  'cell--editing': string
  /**
   * Styles applied to the element that wraps the cell content.
   */
  cellContent: string
  /**
   * Styles applied to the overlay wrapper element.
   */
  overlayWrapper: string
  /**
   * Styles applied to the overlay wrapper inner element.
   */
  overlayWrapperInner: string
  /**
   * Styles applied to the overlay element.
   */
  overlay: string
  /**
   * Styles applied to both the cell and the column header if `showColumnRightBorder={true}`.
   */
  withBorder: string
  /**
   * Styles applied to the column headers wrapper if a column is being dragged.
   */
  columnHeaderDropZone: string
  /**
   * Styles applied to the skeleton cell element.
   */
  cellSkeleton: string
  /**
   * Styles applied to the pinned rows container.
   */
  pinnedRows: string
  /**
   * Styles applied to the top pinned rows container.
   */
  'pinnedRows--top': string
  /**
   * Styles applied to the bottom pinned rows container.
   */
  'pinnedRows--bottom': string
  /**
   * Styles applied to pinned rows render zones.
   */
  pinnedRowsRenderZone: string
  /**
   * Styles applied to the cell Column Header cell element.
   */
  cellColumnHeader: string
  /**
   * class applied when cell has error
   */
  cellError: string
}

const generateGridClasses = (prefix: string) => {
  const classes = [
    'root',
    'root--densityStandard',
    'root--densityComfortable',
    'root--densityCompact',
    'autoHeight',
    'main',
    'columnHeaders',
    'columnHeadersInner',
    'columnHeadersInner--scrollable',
    'pinnedColumnHeaders',
    'pinnedColumnHeaders--left',
    'pinnedColumnHeaders--right',
    'columnHeaderRow',
    'columnHeader',
    'columnHeader--alignCenter',
    'columnHeader--alignLeft',
    'columnHeader--alignRight',
    'columnHeader--sortable',
    'columnHeader--sorted',
    'columnHeader--moving',
    'columnHeader--dragging',
    'columnHeaderDraggableContainer',
    'columnHeaderTitleContainer',
    'columnHeaderTitleContainerContent',
    'columnHeaderTitle',
    'columnHeaderButtonContainer',
    'columnSeparator',
    'columnSeparator--resizable',
    'columnSeparator--resizing',
    'columnSeparator--sideLeft',
    'columnSeparator--sideRight',
    'virtualScroller',
    'virtualScrollerContent',
    'virtualScrollerRenderZone',
    'pinnedColumns',
    'pinnedColumns--left',
    'pinnedColumns--right',
    'row',
    'columnFooterRow',
    'skeletonRow',
    'row--selected',
    'row--dynamicHeight',
    'cell',
    'cell--textCenter',
    'cell--textLeft',
    'cell--textRight',
    'cellContent',
    'overlayWrapper',
    'overlayWrapperInner',
    'overlay',
    'withBorder',
    'columnHeaderDropZone',
    'cellSkeleton',
    'cellColumnHeader',
  ] as const

  const record: unknown = {}

  classes.forEach(element => {
    // @ts-ignore
    record[element] = `${prefix}-${element}`
  })
  return record as GridClasses
}

export const gridClasses = generateGridClasses('DataGrid')
